import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { getAllTemplates } from "../lib/email-storage.ts";
import { type EmailTemplate } from "../lib/email-templates.ts";
import { TemplateViewer } from "../components/email/TemplateViewer.tsx";
import { SendEmailSection } from "../components/email/SendEmailSection.tsx";
import { SentHistorySection } from "../components/email/SentHistorySection.tsx";
import { TemplateEditorModal } from "../components/email/TemplateEditorModal.tsx";

type ActiveTab = "templates" | "send" | "history";

export function EmailsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>("");
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);

  // Tab from URL query param, default to "templates"
  const tabParam = searchParams.get("tab") as ActiveTab | null;
  const initialUserId = searchParams.get("userId") ?? undefined;
  const initialTemplateId = searchParams.get("templateId") ?? undefined;

  const activeTab: ActiveTab =
    tabParam === "send" || tabParam === "history" ? tabParam : "templates";

  function refreshTemplates() {
    setTemplates(getAllTemplates());
  }

  useEffect(() => {
    refreshTemplates();
  }, []);

  useEffect(() => {
    if (initialTemplateId) {
      setSelectedTemplateId(initialTemplateId);
    }
  }, [initialTemplateId]);

  function setTab(tab: ActiveTab) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("tab", tab);
      return next;
    });
  }

  function handleSelectTemplateForSend(tplId: string) {
    setSelectedTemplateId(tplId);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("tab", "send");
      next.set("templateId", tplId);
      return next;
    });
  }

  function handleOpenCreate() {
    setEditingTemplate(null);
    setIsEditorOpen(true);
  }

  function handleOpenEdit(template: EmailTemplate) {
    setEditingTemplate(template);
    setIsEditorOpen(true);
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Email Users</h1>
          <p className="muted">
            Send direct outreach &amp; support emails to users from{" "}
            <span className="mono">mail@currencycore.dev</span> (or{" "}
            <span className="mono">mail@currency-core.com</span>). Replies route to your inbox.
          </p>
        </div>

        <div className="page-head-actions">
          {activeTab === "templates" && (
            <button type="button" className="btn btn-primary" onClick={handleOpenCreate}>
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              New Custom Template
            </button>
          )}

          {activeTab !== "send" && (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setTab("send")}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M22 2L11 13" />
                <polygon points="22 2 15 22 11 13 2 9 22 2" />
              </svg>
              Send Email
            </button>
          )}
        </div>
      </div>

      {/* Cross-Link Banner to Leads */}
      <div className="lead-crosslink-banner mb-4">
        <div className="flex items-center gap-2">
          <span className="badge badge-accent text-xs">New</span>
          <span className="text-xs text-secondary">
            Looking to prospect external apps (like expense management or SaaS tools) that have not registered yet?
          </span>
        </div>
        <Link to="/leads" className="btn btn-secondary btn-xs">
          Open Leads &amp; Outreach →
        </Link>
      </div>

      {/* Main Tab Navigation */}
      <div className="email-tabs-nav">
        <button
          type="button"
          className={`email-nav-tab ${activeTab === "templates" ? "active" : ""}`}
          onClick={() => setTab("templates")}
        >
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <rect x="2" y="4" width="20" height="16" rx="2" />
            <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
          </svg>
          Templates Catalog ({templates.length})
        </button>

        <button
          type="button"
          className={`email-nav-tab ${activeTab === "send" ? "active" : ""}`}
          onClick={() => setTab("send")}
        >
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M22 2L11 13" />
            <polygon points="22 2 15 22 11 13 2 9 22 2" />
          </svg>
          Send Email to Users
        </button>

        <button
          type="button"
          className={`email-nav-tab ${activeTab === "history" ? "active" : ""}`}
          onClick={() => setTab("history")}
        >
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
          Sent History
        </button>
      </div>

      {/* Tab Panels */}
      <div className="email-tab-panel mt-4">
        {activeTab === "templates" && (
          <TemplateViewer
            templates={templates}
            onTemplatesChanged={refreshTemplates}
            onSelectTemplateForSend={handleSelectTemplateForSend}
            onEditTemplate={handleOpenEdit}
          />
        )}

        {activeTab === "send" && (
          <SendEmailSection
            templates={templates}
            selectedTemplateId={selectedTemplateId}
            initialUserId={initialUserId}
            onNavigateToHistory={() => setTab("history")}
          />
        )}

        {activeTab === "history" && <SentHistorySection />}
      </div>

      {/* Editor Modal */}
      <TemplateEditorModal
        isOpen={isEditorOpen}
        template={editingTemplate}
        onClose={() => setIsEditorOpen(false)}
        onSaved={(saved) => {
          refreshTemplates();
          setSelectedTemplateId(saved.id);
        }}
      />
    </>
  );
}
